import { ApolloClient, InMemoryCache, HttpLink, ApolloLink, Observable, split } from '@apollo/client/core';
import { getMainDefinition } from '@apollo/client/utilities';
import { createApolloProvider } from '@vue/apollo-option';
import { DefaultApolloClient } from '@vue/apollo-composable';
import { onError } from '@apollo/client/link/error';
import { setContext } from '@apollo/client/link/context';
import { getRouter } from '@/router';
import { useOperationStore } from '@/store/storeOperation';
import { useBackendHealthStore, classifyTransientError } from '@/store/storeBackendHealth';
import { clientId as cognitoClientId, cognitoDomain } from '@/oidc-client';
import { SSELink } from './sseLink';

/** Turn a GraphQL operation name like `AisVessels` or
 *  `nodeMessageHistory` into the operator-facing verb+noun the
 *  status-bar indicator displays.  Falls back to the raw name when
 *  we can't tokenise it. */
function humaniseOperationName(opName: string): string {
    // Insert a space before each internal capital letter, drop
    // trailing "Query" / "Mutation" / "Subscription" tokens.
    const spaced = opName.replace(/([a-z0-9])([A-Z])/g, '$1 $2');
    const stripped = spaced.replace(/\s+(Query|Mutation|Subscription)$/i, '').trim();
    if (!stripped) return 'Request';
    // Capitalise first word so the badge reads like a sentence
    // fragment: "Loading X" / "Refreshing X".
    return stripped.charAt(0).toUpperCase() + stripped.slice(1);
}

const VITE_GRAPHQL_URL = import.meta.env.VITE_GRAPHQL_URL;
const cache = new InMemoryCache();

// Inject the current operation ID into every request via the X-Operation-Id header.
// Evaluated lazily per-request so Pinia is always initialized when this runs.
const workspaceLink = setContext((_, { headers }) => {
    try {
        const operationStore = useOperationStore();
        const operationId = operationStore.currentOperationId;
        if (!operationId) return { headers };
        return {
            headers: {
                ...headers,
                'X-Operation-Id': operationId,
            },
        };
    } catch {
        // Pinia not yet ready (e.g. during operations query itself)
        return { headers };
    }
});

// Auth link to add JWT token to headers
const authLink = setContext((_, { headers }) => {
    const isDevelopment = import.meta.env.DEV;

    // In development mode, use a dummy token
    if (isDevelopment) {
        return {
            headers: {
                ...headers,
                authorization: 'Bearer DEV_MODE_TOKEN',
            },
        };
    }

    // Cognito access tokens carry the cognito:groups claim and are what the backend's
    // JwtBearer middleware expects to validate.
    const token = localStorage.getItem('access-token');

    // Return the headers to the context so httpLink can read them
    return {
        headers: {
            ...headers,
            authorization: token ? `Bearer ${token}` : '',
        },
    };
});

const graphqlEndpoint = VITE_GRAPHQL_URL ? `${VITE_GRAPHQL_URL}/graphql/` : '/graphql';

const httpLink = new HttpLink({
    uri: graphqlEndpoint,
    //https://www.apollographql.com/docs/react/networking/authentication/#cookie
    credentials: 'include',
});

// SSE link for GraphQL subscriptions (graphql-sse protocol). HotChocolate
// exposes the same /graphql endpoint for HTTP and SSE — differentiated by
// the client's `Accept: text/event-stream` header, which graphql-sse sends
// automatically. Headers are pulled from each operation's context (populated
// by the setContext links above) so subscriptions carry the same auth +
// operation identity as queries.
const sseLink = new SSELink({
    url: graphqlEndpoint,
    credentials: 'include',
    headers: () => {
        // Assemble headers the same way the setContext chain would for
        // HTTP: auth token + operation id. Read at connection open (not
        // every message), so re-login requires a subscription restart.
        const isDevelopment = import.meta.env.DEV;
        const token = isDevelopment
            ? 'DEV_MODE_TOKEN'
            : localStorage.getItem('access-token') ?? '';
        const headers: Record<string, string> = {
            authorization: token ? `Bearer ${token}` : '',
        };
        try {
            const operationId = useOperationStore().currentOperationId;
            if (operationId) headers['X-Operation-Id'] = operationId;
        } catch { /* Pinia not ready yet — subscriber will retry once it is */ }
        return headers;
    },
});

// Silent refresh via Cognito's `/oauth2/token` endpoint using the
// stored refresh token. Called from the errorLink below on any HTTP 401
// BEFORE we fall through to the full /logout redirect — a well-formed
// refresh flow means an access-token expiry doesn't kick the user back
// through Cognito's hosted UI. Only the outer errorLink deals with
// what to do on refresh failure (that's where /logout lives).
//
// De-flapped: two refreshes cannot fire within 3 seconds of each
// other. If several queries 401 in the same tick (subscription
// reconnect + status poll racing an expired token), the first one
// mints a fresh token and updates localStorage; the concurrent ones
// see `_lastRefreshAt < 3s ago`, skip the network call, and re-read
// the freshly-stored token via the setContext replay below.
let _lastRefreshAt = 0;
let _inflightRefresh: Promise<boolean> | null = null;

async function _refreshAccessToken(): Promise<boolean> {
    const refreshToken = localStorage.getItem('refresh-token');
    if (!refreshToken) return false;

    const body = new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: cognitoClientId,
        refresh_token: refreshToken,
    });
    try {
        const res = await fetch(`${cognitoDomain}/oauth2/token`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body,
        });
        if (!res.ok) return false;
        const data: { access_token?: string; id_token?: string; refresh_token?: string } = await res.json();
        if (!data.access_token) return false;
        localStorage.setItem('access-token', data.access_token);
        if (data.id_token) localStorage.setItem('id-token', data.id_token);
        // Cognito does NOT rotate refresh tokens on refresh_token grant
        // by default. If it ever does (config change), pick up the new
        // one so the 30-day validity window keeps sliding forward.
        if (data.refresh_token) localStorage.setItem('refresh-token', data.refresh_token);
        return true;
    } catch {
        return false;
    }
}

function tryRefreshAccessToken(): Promise<boolean> {
    if (_inflightRefresh) return _inflightRefresh;
    const now = Date.now();
    if (now - _lastRefreshAt < 3000) {
        // Recently attempted — assume the previous attempt's outcome
        // already updated localStorage (success) or we're still failing
        // (failure). Read localStorage to decide: if the current token
        // differs from the one that just 401'd, refresh worked.
        return Promise.resolve(!!localStorage.getItem('access-token'));
    }
    _lastRefreshAt = now;
    _inflightRefresh = _refreshAccessToken().finally(() => { _inflightRefresh = null; });
    return _inflightRefresh;
}

// global error handler
const errorLink: ApolloLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
    const router = getRouter();

    if (graphQLErrors) {
        graphQLErrors.forEach(({ extensions }) => {
            if (extensions && extensions.code === 'UNAUTHORIZED') {
                // if unauthorized, redirect to PageNotFound page
                router?.push({ name: 'PageNotFound' });
            }
        });
    }

    // Non-401 HTTP failures + fetch/network drops surface in the
    // status bar's backend-health indicator rather than as a toast.
    // 401 is handled below (auth refresh path).  We describe the
    // failing operation by its GraphQL operation name so operators
    // see e.g. "Refreshing assets — server error" rather than the
    // raw HTTP code.
    if (networkError) {
        const status = (networkError as any).statusCode;
        if (status !== 401 && status !== 403) {
            try {
                const kind = classifyTransientError(networkError) ?? 'unknown';
                const opName = operation?.operationName
                    ? humaniseOperationName(operation.operationName)
                    : 'Request';
                useBackendHealthStore().report(opName, kind);
            } catch (reportErr) {
                // Pinia may not be ready at initial-load time.
                // eslint-disable-next-line no-console
                console.warn('[graphql] failed to record backend health:', reportErr);
            }
        }
    }

    // Handle network errors (including HTTP 401). Sequence:
    //   1. If already on an auth route, do nothing — the auth flow is
    //      already running; a redirect here would race it.
    //   2. Try silent refresh via Cognito's /oauth2/token endpoint with
    //      the stored refresh token. On success, retry the failed
    //      operation with the fresh Bearer header via forward().
    //   3. Only if refresh fails do we clear tokens and push /logout.
    //      /logout — NOT /login — because LoginPage.vue auto-fires
    //      signinRedirect() and Cognito's SSO cookie persists across a
    //      client-side token wipe; bouncing to /login otherwise loops
    //      through /login-callback.
    if (networkError && 'statusCode' in networkError && networkError.statusCode === 401) {
        const routeName = router?.currentRoute.value.name;
        const alreadyOnAuthRoute = routeName === 'Login'
            || routeName === 'LoginCallback'
            || routeName === 'Logout';
        if (alreadyOnAuthRoute) return;

        return new Observable(observer => {
            tryRefreshAccessToken().then(refreshed => {
                if (refreshed) {
                    const newToken = localStorage.getItem('access-token') ?? '';
                    // Overwrite the Bearer header on the retry — authLink
                    // ran before errorLink so the operation's context
                    // still carries the *old* token that just 401'd.
                    operation.setContext(({ headers = {} }: { headers?: Record<string, string> }) => ({
                        headers: { ...headers, authorization: `Bearer ${newToken}` },
                    }));
                    forward(operation).subscribe({
                        next:     observer.next.bind(observer),
                        error:    observer.error.bind(observer),
                        complete: observer.complete.bind(observer),
                    });
                    return;
                }
                // Refresh failed — clear tokens and hand off to the
                // logout flow. Recheck the current route in case the
                // refresh fetch took long enough for us to have
                // navigated elsewhere already.
                const nowRoute = router?.currentRoute.value.name;
                if (nowRoute !== 'Login' && nowRoute !== 'LoginCallback' && nowRoute !== 'Logout') {
                    localStorage.removeItem('access-token');
                    localStorage.removeItem('id-token');
                    localStorage.removeItem('refresh-token');
                    localStorage.removeItem('email');
                    router?.push({ name: 'Logout' });
                }
                observer.error(networkError);
            }).catch(err => observer.error(err));
        });
    }
});

// Route `subscription` operations to the SSE link and everything else
// (queries + mutations) through the HTTP link. The context-setting links
// (operation + auth) sit *above* the split so they populate the headers
// the HTTP path uses; the SSE link resolves its own headers via its
// `headers()` factory since setContext values aren't visible to it.
const transportLink = split(
    ({ query }) => {
        const def = getMainDefinition(query);
        return def.kind === 'OperationDefinition' && def.operation === 'subscription';
    },
    sseLink,
    httpLink,
);

const fullLink = ApolloLink.from([workspaceLink, authLink, errorLink, transportLink]);

export const apolloClient = new ApolloClient({
    cache,
    link: fullLink,
    devtools: {
        enabled: true,
    },
    defaultOptions: {
        watchQuery: {
            fetchPolicy: 'no-cache',
            errorPolicy: 'ignore',
        },
        query: {
            fetchPolicy: 'no-cache',
            errorPolicy: 'all',
        },
    },
});

/**
 * Retrieves the global Apollo GraphQL client.
 *
 * @returns {ApolloClient}
 */
function getApolloClient() {
    return apolloClient;
}

const apolloProvider = createApolloProvider({
    defaultClient: apolloClient,
});

export default {
    apolloProvider: apolloProvider,
    getClient: getApolloClient,
    DefaultApolloClient, // Export for Composition API
};
